import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { label: "Work", to: "/work" },
  { label: "Services", to: "/services" },
  { label: "Process", to: "/process" },
  { label: "About", to: "/#about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToHash = (hash) => {
    document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" });
  };

  const go = (to) => {
    setOpen(false);
    if (to.startsWith("/#")) {
      const hash = to.slice(1);
      if (location.pathname === "/") {
        scrollToHash(hash);
      } else {
        navigate("/");
        setTimeout(() => scrollToHash(hash), 120);
      }
    } else {
      navigate(to);
    }
  };

  const goQuote = () => {
    setOpen(false);
    if (document.querySelector("#quote")) {
      scrollToHash("#quote");
    } else {
      navigate("/");
      setTimeout(() => scrollToHash("#quote"), 120);
    }
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-5 md:px-10">
        <button
          data-testid="nav-logo"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-left"
        >
          <span className="font-display text-xl font-bold uppercase tracking-tight leading-none">
            Visions
          </span>
          <span className="h-1.5 w-1.5 rounded-full bg-[#FF3B30]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 hidden sm:block">
            Displays
          </span>
        </button>

        <div className="hidden items-center gap-10 md:flex">
          {links.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.to)}
              className="font-mono text-xs uppercase tracking-[0.18em] text-white/60 transition-colors hover:text-white"
            >
              {l.label}
            </button>
          ))}
        </div>

        <button
          data-testid="nav-quote-btn"
          onClick={goQuote}
          className="group hidden items-center gap-2 rounded-full bg-white px-6 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-black transition-colors hover:bg-[#FF3B30] hover:text-white md:flex"
        >
          Get a Quote
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </button>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="glass overflow-hidden border-t border-white/10 md:hidden"
          >
            <div className="flex flex-col gap-4 px-6 py-8">
              {links.map((l) => (
                <button
                  key={l.label}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.to)}
                  className="text-left font-display text-2xl font-medium uppercase tracking-tight"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-quote-btn"
                onClick={goQuote}
                className="mt-4 rounded-full bg-[#FF3B30] px-6 py-3 font-mono text-xs uppercase tracking-[0.15em]"
              >
                Get a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
